import type { z } from "zod";
import buildParams from "../searchParams/buildParams";
import getOrigin from "./getOrigin";
import {
  matchPath,
  useParams,
  useSearchParams,
  type Params,
} from "@remix-run/react";
import getSearchParams from "../searchParams/getSearchParams";
import isNil from "../validation/isNil";
import isPresent from "../validation/isPresent";

type Options = {
  origin?: string;
};

/**
 * @deprecated in favor of template.fromRoute
 **/
export default function route<T extends any[]>(
  definition: string | ((...props: T) => string),
  options: Options = {},
) {
  const buildPath =
    typeof definition === "string" ? () => definition : definition;
  return {
    url: (...props: T) => {
      const origin = options.origin ?? getOrigin();
      return `${origin}${buildPath(...props)}`;
    },
    path: buildPath,
  };
}

type TemplateParamsSchema = z.ZodObject<any>;
type TemplateQuerySchema = z.ZodObject<any>;

type TemplateRouteOptions<
  TParamsSchema extends TemplateParamsSchema = TemplateParamsSchema,
  TQuerySchema extends TemplateQuerySchema = TemplateQuerySchema,
> = Options & {
  params?: TParamsSchema;
  query?: TQuerySchema;
};

export type TemplateRouteArgs<TOptions extends TemplateRouteOptions> = {
  encodeURIComponents?: boolean;
} & (TOptions extends TemplateRouteOptions<
  infer TParamsSchema,
  infer TQuerySchema
>
  ? TOptions["params"] extends TemplateParamsSchema
    ? TOptions["query"] extends TemplateQuerySchema
      ? { query?: z.infer<TQuerySchema>; params: z.infer<TParamsSchema> }
      : { query?: never; params: z.infer<TParamsSchema> }
    : TOptions["query"] extends TemplateQuerySchema
    ? { query?: z.infer<TQuerySchema>; params?: never }
    : never
  : never);

function fromTemplate<TOptions extends TemplateRouteOptions>(
  template: string,
  options: TOptions = {} as TOptions,
) {
  type RouteArgs = TemplateRouteArgs<TOptions> extends never
    ? []
    : [TemplateRouteArgs<TOptions>];

  const baseRoute = route((...opts: RouteArgs) => {
    if (hasNoParams(opts)) {
      return template;
    }

    let result = template;

    if (opts[0].params) {
      result = Object.entries(opts[0].params).reduce((acc, [key, value]) => {
        if (isNil(value)) {
          return acc;
        }

        return acc.replace(
          new RegExp(`:${key}`, "g"),
          (value as any).toString(),
        );
      }, template);
    }

    if (opts[0].query) {
      result =
        result +
        buildParams(opts[0].query, {
          encodeURIComponents: opts[0]?.encodeURIComponents ?? true,
        });
    }

    return result;
  }, options);

  return {
    ...baseRoute,
    parseQuery: (request: Request) => {
      const searchParams = getSearchParams(request);
      const searchObj = Object.fromEntries(searchParams.entries());
      const parseResult = options.query
        ? options.query.safeParse(searchObj)
        : { success: true, data: {} };

      if (parseResult.success) {
        return parseResult.data as TemplateRouteArgs<TOptions>["query"];
      }

      throw new Response("Invalid query params provided", { status: 400 });
    },
    getMatch: (url: string) => {
      const match = matchPath(template, url);

      if (isPresent(match)) {
        return {
          isMatch: true as const,
          params: match.params as TemplateRouteArgs<TOptions>["params"],
        };
      }

      return {
        isMatch: false as const,
        params: undefined,
      };
    },
    useQuery: () => {
      const [searchParams, setSearchParams] = useSearchParams();
      const obj = Object.fromEntries(searchParams.entries());
      const parseResult = options.query
        ? options.query.safeParse(obj)
        : { success: true, data: {} };

      if (parseResult.success) {
        return [
          parseResult.data as TemplateRouteArgs<TOptions>["query"],
          setSearchParams,
        ] as const;
      }

      return [
        {} as TemplateRouteArgs<TOptions>["query"],
        setSearchParams,
      ] as const;
    },
    useParams: () => {
      const params = useParams<Params>();
      const parseResult = options.params
        ? options.params.safeParse(params)
        : { success: true, data: {} };

      if (parseResult.success) {
        return parseResult.data as TemplateRouteArgs<TOptions>["params"];
      }

      throw new Response("Required params not provided", { status: 400 });
    },
    parseParams: (params: Params) => {
      const parseResult = options.params
        ? options.params.safeParse(params)
        : { success: true, data: {} };

      if (parseResult.success) {
        return parseResult.data as TemplateRouteArgs<TOptions>["params"];
      }

      throw new Response("Required params not provided", { status: 400 });
    },
  };
}

function hasNoParams(params: unknown[]): params is [] {
  return params.length === 0;
}

route.fromTemplate = fromTemplate;
